import { useState } from 'react';
import { useTheme } from 'styled-components';
import { IconButton, Menu, MenuItem, Popover, Button } from '@workramp/ui';
import NotificationContent from '~/components/navigation/NotificationContent';
import { useMediaQuery } from '~/hooks';
import { NotificationsNavItem } from '~/components/navigation/NotificationsNavItem';
import { LearnerLanguage } from '~/elc/features/learner/components/LearnerLanguage';
import { useUserProfileQuery } from '../../api';
import { LEARNER_ROUTES } from '../../constants';
import { LearnerAvatar } from '../LearnerAvatar';
import { AvatarButton, MenuWrapper, NavItem } from './Header.styles';

const { I18n } = window;

const NOTIFICATIONS_MENU_OPTION = 'notifications';

export interface HeaderMenuProps {
  userId: number;
  brandColor?: string;
  isWhiteLabel?: boolean;
  showAdminConsole?: boolean;
  onLogout: () => void;
}

export function HeaderMenu({ userId, brandColor, isWhiteLabel, showAdminConsole, onLogout }: HeaderMenuProps) {
  // Dynamically render some items based on viewport
  const theme = useTheme();
  const isSmAndUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isMdAndUp = useMediaQuery(theme.breakpoints.up('md'));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationPopoverOpen, setIsNotificationPopoverOpen] = useState(false);

  const TRANSLATED_SAVED_CONTENT = I18n.t('js.learner.saved_content.saved_content');
  const TRANSLATED_PROFILE = I18n.t('js.learner.home.sidebar.profile');
  const TRANSLATED_SETTINGS = I18n.t('js.learner.home.sidebar.settings');
  const TRANSLATED_SUPPORT = I18n.t('js.learner.home.sidebar.support');
  const TRANSLATED_LOGOUT = I18n.t('js.learner.home.sidebar.logout');
  const TRANSLATED_ADMIN_CONSOLE = I18n.t('js.learner.home.sidebar.admin_console');
  const TRANSLATED_NOTIFICATIONS = I18n.t('js.learner.notifications.dropdown.heading');
  const TRANSLATED_MENU_LABEL = I18n.t('js.learner.home.sidebar.profile_menu_label');

  const profileQuery = useUserProfileQuery(userId);

  const handleMenuSelect = (item: string | number) => {
    if (item === LEARNER_ROUTES.logout) {
      onLogout();
    } else if (item === NOTIFICATIONS_MENU_OPTION) {
      setIsNotificationPopoverOpen(true);
    }
  };

  if (!profileQuery.data) {
    return null;
  }

  const { displayName, avatar } = profileQuery.data;

  const menuButton = (
    <AvatarButton
      aria-label={TRANSLATED_MENU_LABEL}
      tooltip={{
        message: TRANSLATED_MENU_LABEL,
        placement: 'bottom-end',
      }}
    >
      <LearnerAvatar name={displayName} imgUrl={avatar} brandColor={brandColor} size="md" />
    </AvatarButton>
  );

  return (
    <MenuWrapper>
      {!isWhiteLabel && isSmAndUp && (
        <IconButton
          href={LEARNER_ROUTES.savedContent}
          variant="text"
          color="secondary"
          iconName="bookmark:regular"
          label={TRANSLATED_SAVED_CONTENT}
          size="lg"
          tooltip={{
            message: TRANSLATED_SAVED_CONTENT,
            placement: 'bottom-end',
          }}
        />
      )}
      {!isWhiteLabel && isSmAndUp && (
        <IconButton
          href={LEARNER_ROUTES.help}
          variant="text"
          color="secondary"
          iconName="help:regular"
          label={TRANSLATED_SUPPORT}
          size="lg"
          tooltip={{
            message: TRANSLATED_SUPPORT,
            placement: 'bottom-end',
          }}
        />
      )}
      {!isWhiteLabel && isSmAndUp && (
        <NotificationsNavItem
          userId={userId}
          isOpen={isNotificationPopoverOpen}
          onOpenChange={setIsNotificationPopoverOpen}
        />
      )}

      {/* Show a language dropdown only for white label */}
      {isWhiteLabel && <LearnerLanguage />}

      {/* Show the menu unless we are in a mobile viewport with the notification popover open */}
      {!isWhiteLabel && ((!isSmAndUp && !isNotificationPopoverOpen) || isSmAndUp) && (
        <Menu
          placement="bottom-end"
          isOpen={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          onAction={handleMenuSelect}
          buttonEl={menuButton}
        >
          <MenuItem key={LEARNER_ROUTES.profile} href={LEARNER_ROUTES.profile} iconName="profile:regular">
            {TRANSLATED_PROFILE}
          </MenuItem>
          <MenuItem key={LEARNER_ROUTES.settings} href={LEARNER_ROUTES.settings} iconName="controls-cog:regular">
            {TRANSLATED_SETTINGS}
          </MenuItem>
          {!isSmAndUp && (
            <MenuItem key={LEARNER_ROUTES.savedContent} href={LEARNER_ROUTES.savedContent} iconName="bookmark:regular">
              {TRANSLATED_SAVED_CONTENT}
            </MenuItem>
          )}
          {!isSmAndUp && (
            <MenuItem key="notifications" iconName="notification:regular">
              {TRANSLATED_NOTIFICATIONS}
            </MenuItem>
          )}
          {!isSmAndUp && (
            <MenuItem key={LEARNER_ROUTES.help} href={LEARNER_ROUTES.help} iconName="help:regular">
              {TRANSLATED_SUPPORT}
            </MenuItem>
          )}
          <MenuItem key={LEARNER_ROUTES.logout} iconName="exit:regular">
            {TRANSLATED_LOGOUT}
          </MenuItem>
        </Menu>
      )}

      {!isSmAndUp && isNotificationPopoverOpen && (
        <Popover
          isOpen={isNotificationPopoverOpen}
          onOpenChange={setIsNotificationPopoverOpen}
          placement="bottom-end"
          title={TRANSLATED_NOTIFICATIONS}
          buttonEl={menuButton}
        >
          {/* @ts-expect-error: NotificationContent is not typed */}
          <NotificationContent user={{ id: userId }} />
        </Popover>
      )}

      {isMdAndUp && showAdminConsole && (
        <NavItem>
          <Button
            variant="text-inline"
            color="secondary"
            trailingIconName="arrow-right:regular"
            href={LEARNER_ROUTES.admin}
          >
            {TRANSLATED_ADMIN_CONSOLE}
          </Button>
        </NavItem>
      )}
    </MenuWrapper>
  );
}
